import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { PlusCircle, Edit, Trash2, FileText, Eye, Image, Filter, Copy } from "lucide-react";
import DocumentToBlog from "@/components/DocumentToBlog";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import { useAuditLog } from "@/hooks/useAuditLog";

import BlogImageUpload from "@/components/BlogImageUpload";
import BlogAttachments, { Attachment } from "@/components/BlogAttachments";
import RichTextEditor, { RichTextEditorRef } from "@/components/RichTextEditor";
import { useAuth } from "@/hooks/useAuth";
import { Textarea } from "@/components/ui/textarea";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  category: string;
  status: string;
  author_id: string;
  blog_types: string[];
  created_at: string;
  updated_at: string;
  published_at: string | null;
  publish_at: string | null;
  featured_image_url?: string;
  attachments?: Attachment[];
  author_name?: string;
}

// A datetime-local input shows and returns LOCAL wall-clock time, but toISOString() renders UTC.
// Feeding one into the other drifts the value by the UTC offset on every save: in BST, 08:00 is
// stored as 07:00Z, redisplayed as 07:00, then saved as 06:00Z. Harmless on a display date, not
// harmless on a schedule, so both directions go through these.
const toLocalInputValue = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const fromLocalInputValue = (value: string): string | null =>
  value ? new Date(value).toISOString() : null;

interface StaffMember {
  id: string;
  first_name: string;
  last_name: string;
  role_level: 'user' | 'admin' | 'superadmin' | 'adviser';
}

interface ProfileWithStaff {
  user_id: string;
  staff_member_id: string;
  staff_members: StaffMember;
}

const BlogManagement = () => {
  const { user, staffMember, loading: authLoading } = useAuth();
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>([]);
  const [blogTypeFilter, setBlogTypeFilter] = useState<string>('all');
  const [availableAuthors, setAvailableAuthors] = useState<ProfileWithStaff[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState<'new' | 'edit' | null>(null);
  const newEditorRef = useRef<RichTextEditorRef>(null);
  const editEditorRef = useRef<RichTextEditorRef>(null);
  const [newPost, setNewPost] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: '',
    status: 'draft',
    author_id: '',
    featured_image_url: '',
    blog_types: ['public'] as string[],
    attachments: [] as Attachment[]
  });
  const { toast } = useToast();
  const { logStaffAccess } = useAuditLog();

  useEffect(() => {
    if (user && staffMember) {
      fetchBlogPosts();
      fetchAvailableAuthors();
    }
  }, [user, staffMember]);

  useEffect(() => {
    // Filter posts based on blog type filter
    if (blogTypeFilter === 'all') {
      setFilteredPosts(blogPosts);
    } else if (blogTypeFilter === 'public') {
      setFilteredPosts(blogPosts.filter(post => post.blog_types.includes('public')));
    } else if (blogTypeFilter === 'insights') {
      setFilteredPosts(blogPosts.filter(post => post.blog_types.includes('insights')));
    } else if (blogTypeFilter === 'both') {
      setFilteredPosts(blogPosts.filter(post => 
        post.blog_types.includes('public') && post.blog_types.includes('insights')
      ));
    }
  }, [blogPosts, blogTypeFilter]);

  const fetchAvailableAuthors = async () => {
    try {
      const { data, error } = await supabase.rpc('get_blog_authors');

      if (error) throw error;
      
      // Transform data to match expected format
      const transformedData = (data || []).map(author => ({
        user_id: author.user_id,
        staff_member_id: author.staff_member_id,
        staff_members: {
          id: author.staff_member_id,
          first_name: author.first_name,
          last_name: author.last_name,
          role_level: author.role_level
        }
      }));
      
      setAvailableAuthors(transformedData);
    } catch (error) {
      console.error('Error fetching available authors:', error);
    }
  };

  const fetchBlogPosts = async () => {
    try {
      if (!user || !staffMember) {
        console.error('Cannot fetch blog posts - user or staffMember missing:', { user: !!user, staffMember: !!staffMember });
        setLoading(false);
        return;
      }

      console.log('Fetching blog posts with user:', user.id, 'role:', staffMember.role_level);

      let query = supabase
        .from('blog_posts')
        .select('*');

      // If current user is an adviser, only show their own posts
      if (staffMember.role_level === 'adviser') {
        query = query.eq('author_id', user.id);
      }

      const { data, error } = await query.order('created_at', { ascending: false });
      
      console.log('Blog posts query result:', { data: data?.length, error });
      
      if (error) throw error;
      
      // Fetch author names for each post
      const postsWithAuthors = await Promise.all(
        (data || []).map(async (post) => {
          const { data: authorData } = await supabase
            .rpc('get_blog_author', { author_user_id: post.author_id });
          
          return {
            ...post,
            attachments: (Array.isArray(post.attachments) ? post.attachments as unknown as Attachment[] : []),
            author_name: authorData && authorData.length > 0 
              ? `${authorData[0].first_name} ${authorData[0].last_name}`
              : 'Unknown Author'
          } as BlogPost;
        })
      );
      
      setBlogPosts(postsWithAuthors);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      toast({
        title: "Error",
        description: "Failed to load blog posts",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const createSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .trim();
  };

  const createBlogPost = async () => {
    if (!staffMember) return;

    try {
      const slug = newPost.slug || createSlug(newPost.title);
      const authorId = newPost.author_id || (await supabase.auth.getUser()).data.user?.id;
      
      const { error } = await supabase
        .from('blog_posts')
        .insert([{
          title: newPost.title,
          slug,
          excerpt: newPost.excerpt,
          content: newPost.content,
          category: newPost.category,
          status: newPost.status,
          author_id: authorId,
          featured_image_url: newPost.featured_image_url,
          blog_types: newPost.blog_types,
          attachments: JSON.parse(JSON.stringify(newPost.attachments)),
          published_at: newPost.status === 'published' ? new Date().toISOString() : null
        }]);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Blog post created successfully"
      });
      
      fetchBlogPosts();
      setIsCreateDialogOpen(false);
      setNewPost({
        title: '',
        slug: '',
        excerpt: '',
        content: '',
        category: '',
        status: 'draft',
        author_id: '',
        featured_image_url: '',
        blog_types: ['public'],
        attachments: []
      });
    } catch (error) {
      console.error('Error creating blog post:', error);
      toast({
        title: "Error",
        description: "Failed to create blog post",
        variant: "destructive"
      });
    }
  };

  const updateBlogPost = async () => {
    if (!editingPost) return;

    try {
      const { error } = await supabase
        .from('blog_posts')
        .update({
          title: editingPost.title,
          slug: editingPost.slug,
          excerpt: editingPost.excerpt,
          content: editingPost.content,
          category: editingPost.category,
          status: editingPost.status,
          author_id: editingPost.author_id,
          featured_image_url: editingPost.featured_image_url,
          blog_types: editingPost.blog_types,
          attachments: JSON.parse(JSON.stringify(editingPost.attachments || [])),
          published_at: editingPost.status === 'published' && !editingPost.published_at
            ? new Date().toISOString()
            : editingPost.published_at,
          // Only a draft can carry a schedule. Publishing by hand clears it so the cron
          // sweep cannot re-stamp published_at afterwards.
          publish_at: editingPost.status === 'draft' ? editingPost.publish_at : null
        })
        .eq('id', editingPost.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Blog post updated successfully"
      });
      
      fetchBlogPosts();
      setIsEditDialogOpen(false);
      setEditingPost(null);
    } catch (error) {
      console.error('Error updating blog post:', error);
      toast({
        title: "Error",
        description: "Failed to update blog post",
        variant: "destructive"
      });
    }
  };

  const deleteBlogPost = async (id: string) => {
    if (!confirm('Are you sure you want to delete this blog post?')) return;

    try {
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Blog post deleted successfully"
      });
      
      fetchBlogPosts();
    } catch (error) {
      console.error('Error deleting blog post:', error);
      toast({
        title: "Error",
        description: "Failed to delete blog post",
        variant: "destructive"
      });
    }
  };

  const duplicateBlogPost = async (post: BlogPost) => {
    if (!staffMember || !user) return;

    try {
      const newSlug = `${post.slug}-copy-${Date.now()}`;
      const { error } = await supabase
        .from('blog_posts')
        .insert([{
          title: `${post.title} (Copy)`,
          slug: newSlug,
          excerpt: post.excerpt,
          content: post.content,
          category: post.category,
          status: 'draft',
          author_id: user.id,
          featured_image_url: post.featured_image_url,
          blog_types: post.blog_types,
          attachments: JSON.parse(JSON.stringify(post.attachments || [])),
          published_at: null
        }]);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Blog post duplicated as draft"
      });
      
      fetchBlogPosts();
    } catch (error) {
      console.error('Error duplicating blog post:', error);
      toast({
        title: "Error",
        description: "Failed to duplicate blog post",
        variant: "destructive"
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'published':
        return <Badge variant="default">Published</Badge>;
      case 'draft':
        return <Badge variant="secondary">Draft</Badge>;
      case 'archived':
        return <Badge variant="outline">Archived</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const canEdit = (post: BlogPost) => {
    if (!staffMember || !user) return false;
    
    // Admins and superadmins can edit any post
    if (staffMember.role_level === 'admin' || staffMember.role_level === 'superadmin') {
      return true;
    }
    
    // For advisers, they can only edit their own unpublished posts
    if (staffMember.role_level === 'adviser') {
      // Check if it's their own post AND it's not published
      return post.author_id === user.id && post.status !== 'published';
    }
    
    return false;
  };

  const canDelete = () => {
    return staffMember?.role_level === 'admin' || staffMember?.role_level === 'superadmin';
  };

  if (authLoading || loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  if (!user || !staffMember) {
    return <div className="flex justify-center items-center h-64">Please log in to access this page.</div>;
  }

  return (
    <>
      <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Blog Management</h1>
          <p className="text-muted-foreground mt-2">Create and manage blog posts</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <PlusCircle className="w-4 h-4 mr-2" />
            Create Post
          </Button>
          <BlogImageUpload 
            trigger={
              <Button variant="outline">
                <Image className="w-4 h-4 mr-2" />
                Manage Images
              </Button>
            }
          />
        </div>
      </div>

      {/* Filter Controls */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Label className="text-sm font-medium">Filter by Blog Type:</Label>
            </div>
            <div className="flex gap-2">
              <Button
                variant={blogTypeFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setBlogTypeFilter('all')}
              >
                All
              </Button>
              <Button
                variant={blogTypeFilter === 'public' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setBlogTypeFilter('public')}
              >
                Public Blog
              </Button>
              <Button
                variant={blogTypeFilter === 'insights' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setBlogTypeFilter('insights')}
              >
                Insights
              </Button>
              <Button
                variant={blogTypeFilter === 'both' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setBlogTypeFilter('both')}
              >
                Both
              </Button>
            </div>
            <div className="ml-auto text-sm text-muted-foreground">
              Showing {filteredPosts.length} of {blogPosts.length} posts
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Blog Posts
          </CardTitle>
          <CardDescription>
            Manage your blog content and publications
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Author</TableHead>
                <TableHead>Blog Type</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Published</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPosts.map((post) => (
                <TableRow key={post.id}>
                  <TableCell className="font-medium">{post.title}</TableCell>
                  <TableCell>{post.author_name}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {post.blog_types.includes('public') && (
                        <Badge variant="outline" className="text-xs">Public</Badge>
                      )}
                      {post.blog_types.includes('insights') && (
                        <Badge variant="secondary" className="text-xs">Insights</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{post.category}</TableCell>
                  <TableCell>{getStatusBadge(post.status)}</TableCell>
                  <TableCell>{post.published_at ? new Date(post.published_at).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>{new Date(post.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                      >
                        <Link to={post.blog_types?.includes('insights') ? `/insights/${post.slug}` : `/blog/${post.slug}`}>
                          <Eye className="w-4 h-4" />
                        </Link>
                      </Button>
                      {canEdit(post) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingPost(post);
                            setIsEditDialogOpen(true);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      )}
                      {canDelete() && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => duplicateBlogPost(post)}
                            title="Duplicate post"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteBlogPost(post.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Blog Post</DialogTitle>
            <DialogDescription>
              Create a new blog post for your website.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 pb-2 border-b">
            <span className="text-sm text-muted-foreground">Quick start:</span>
            <DocumentToBlog
              onContentExtracted={({ title, content, excerpt }) => {
                setNewPost({
                  ...newPost,
                  title,
                  slug: createSlug(title),
                  content,
                  excerpt
                });
              }}
            />
          </div>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={newPost.title}
                onChange={(e) => {
                  const title = e.target.value;
                  setNewPost({ 
                    ...newPost, 
                    title,
                    slug: createSlug(title)
                  });
                }}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="slug">Slug</Label>
              <Input
                id="slug"
                value={newPost.slug}
                onChange={(e) => setNewPost({ ...newPost, slug: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="author">Author</Label>
              <Select 
                value={newPost.author_id} 
                onValueChange={(value) => setNewPost({ ...newPost, author_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select author" />
                </SelectTrigger>
                <SelectContent>
                  {availableAuthors.map((author) => (
                    <SelectItem key={author.user_id} value={author.user_id}>
                      {author.staff_members.first_name} {author.staff_members.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="category">Category</Label>
              <Input
                id="category"
                value={newPost.category}
                onChange={(e) => setNewPost({ ...newPost, category: e.target.value })}
                placeholder="e.g., Investment Insights, Market Analysis"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="excerpt">Excerpt</Label>
              <Textarea
                id="excerpt"
                value={newPost.excerpt}
                onChange={(e) => setNewPost({ ...newPost, excerpt: e.target.value })}
                placeholder="Brief description of the post..."
                rows={3}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="featured_image">Featured Image URL</Label>
              <div className="flex gap-2">
                <Input
                  id="featured_image"
                  value={newPost.featured_image_url}
                  onChange={(e) => setNewPost({ ...newPost, featured_image_url: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                />
                <BlogImageUpload
                  onImageInsert={(imageUrl) => {
                    setNewPost({ ...newPost, featured_image_url: imageUrl.replace(/!\[.*?\]\((.*?)\)/, '$1') });
                  }}
                  trigger={
                    <Button type="button" variant="outline" size="sm">
                      <Image className="w-4 h-4" />
                    </Button>
                  }
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Content</Label>
              <RichTextEditor
                ref={newEditorRef}
                content={newPost.content}
                onChange={(content) => setNewPost({ ...newPost, content })}
                onImageRequest={() => setShowImagePicker('new')}
              />
            </div>
            <BlogAttachments
              id="create"
              attachments={newPost.attachments}
              onChange={(attachments) => setNewPost({ ...newPost, attachments })}
            />
            <div className="grid gap-2">
              <Label htmlFor="status">Status</Label>
              <Select value={newPost.status} onValueChange={(value) => setNewPost({ ...newPost, status: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Blog Type</Label>
              <div className="flex flex-col gap-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="blog-type-public"
                    checked={newPost.blog_types.includes('public')}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setNewPost({ ...newPost, blog_types: [...newPost.blog_types, 'public'] });
                      } else {
                        setNewPost({ ...newPost, blog_types: newPost.blog_types.filter(t => t !== 'public') });
                      }
                    }}
                  />
                  <Label htmlFor="blog-type-public" className="font-normal cursor-pointer">
                    Public Blog - Visible to everyone
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="blog-type-insights"
                    checked={newPost.blog_types.includes('insights')}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setNewPost({ ...newPost, blog_types: [...newPost.blog_types, 'insights'] });
                      } else {
                        setNewPost({ ...newPost, blog_types: newPost.blog_types.filter(t => t !== 'insights') });
                      }
                    }}
                  />
                  <Label htmlFor="blog-type-insights" className="font-normal cursor-pointer">
                    Partner Insights - Partner only
                  </Label>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Select at least one blog type</p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={createBlogPost}>Create Post</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Blog Post</DialogTitle>
            <DialogDescription>
              Make changes to your blog post.
            </DialogDescription>
          </DialogHeader>
          {editingPost && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="editTitle">Title</Label>
                <Input
                  id="editTitle"
                  value={editingPost.title}
                  onChange={(e) => setEditingPost({ ...editingPost, title: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="editSlug">Slug</Label>
                <Input
                  id="editSlug"
                  value={editingPost.slug}
                  onChange={(e) => setEditingPost({ ...editingPost, slug: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="editAuthor">Author</Label>
                <Select 
                  value={editingPost.author_id} 
                  onValueChange={(value) => setEditingPost({ ...editingPost, author_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableAuthors.map((author) => (
                      <SelectItem key={author.user_id} value={author.user_id}>
                        {author.staff_members.first_name} {author.staff_members.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="editCategory">Category</Label>
                <Input
                  id="editCategory"
                  value={editingPost.category}
                  onChange={(e) => setEditingPost({ ...editingPost, category: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="editExcerpt">Excerpt</Label>
                <Textarea
                  id="editExcerpt"
                  value={editingPost.excerpt}
                  onChange={(e) => setEditingPost({ ...editingPost, excerpt: e.target.value })}
                  rows={3}
                />
              </div>
               <div className="grid gap-2">
                 <Label htmlFor="editContent">Featured Image URL</Label>
                 <div className="flex gap-2">
                   <Input
                     id="edit_featured_image"
                     value={editingPost?.featured_image_url || ''}
                     onChange={(e) => editingPost && setEditingPost({ ...editingPost, featured_image_url: e.target.value })}
                     placeholder="https://example.com/image.jpg"
                   />
                   <BlogImageUpload
                     onImageInsert={(imageUrl) => {
                       if (editingPost) {
                         setEditingPost({ ...editingPost, featured_image_url: imageUrl.replace(/!\[.*?\]\((.*?)\)/, '$1') });
                       }
                     }}
                     trigger={
                       <Button type="button" variant="outline" size="sm">
                         <Image className="w-4 h-4" />
                       </Button>
                     }
                   />
                 </div>
               </div>
               <div className="grid gap-2">
                 <Label>Content</Label>
                 <RichTextEditor
                   ref={editEditorRef}
                   content={editingPost.content}
                   onChange={(content) => setEditingPost({ ...editingPost, content })}
                   onImageRequest={() => setShowImagePicker('edit')}
                 />
               </div>
               <BlogAttachments
                 id="edit"
                 attachments={editingPost.attachments || []}
                 onChange={(attachments) => setEditingPost({ ...editingPost, attachments })}
               />
                <div className="grid gap-2">
                  <Label htmlFor="editPublishedAt">Published Date</Label>
                  <Input
                    id="editPublishedAt"
                    type="datetime-local"
                    value={toLocalInputValue(editingPost.published_at)}
                    onChange={(e) => setEditingPost({ 
                      ...editingPost, 
                      published_at: fromLocalInputValue(e.target.value)
                    })}
                  />
                  <p className="text-xs text-muted-foreground">
                    Display and sort date only. Setting this to a future date does not hold a post
                    back; use Schedule publish below for that.
                  </p>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="editPublishAt">Schedule publish</Label>
                  <Input
                    id="editPublishAt"
                    type="datetime-local"
                    disabled={editingPost.status !== 'draft'}
                    value={toLocalInputValue(editingPost.publish_at)}
                    onChange={(e) => setEditingPost({
                      ...editingPost,
                      publish_at: fromLocalInputValue(e.target.value)
                    })}
                  />
                  <p className="text-xs text-muted-foreground">
                    {editingPost.status === 'draft'
                      ? 'Leave the status as Draft and set a date and time here. The post goes live on its own within five minutes of that time, and its published date is stamped with it. Clear the field to cancel.'
                      : 'Only a draft can be scheduled. Set the status to Draft to schedule this post.'}
                  </p>
                </div>
                 <div className="grid gap-2">
                   <Label htmlFor="editStatus">Status</Label>
                   <Select 
                     value={editingPost.status} 
                     onValueChange={(value) => 
                       setEditingPost({ ...editingPost, status: value })
                     }
                   >
                     <SelectTrigger>
                       <SelectValue />
                     </SelectTrigger>
                     <SelectContent>
                       <SelectItem value="draft">Draft</SelectItem>
                       <SelectItem value="published">Published</SelectItem>
                       <SelectItem value="archived">Archived</SelectItem>
                     </SelectContent>
                   </Select>
                 </div>
                 <div className="grid gap-2">
                   <Label>Blog Type</Label>
                   <div className="flex flex-col gap-3">
                     <div className="flex items-center space-x-2">
                       <Checkbox
                         id="edit-blog-type-public"
                         checked={editingPost.blog_types.includes('public')}
                         onCheckedChange={(checked) => {
                           if (checked) {
                             setEditingPost({ ...editingPost, blog_types: [...editingPost.blog_types, 'public'] });
                           } else {
                             setEditingPost({ ...editingPost, blog_types: editingPost.blog_types.filter(t => t !== 'public') });
                           }
                         }}
                       />
                       <Label htmlFor="edit-blog-type-public" className="font-normal cursor-pointer">
                         Public Blog - Visible to everyone
                       </Label>
                     </div>
                     <div className="flex items-center space-x-2">
                       <Checkbox
                         id="edit-blog-type-insights"
                         checked={editingPost.blog_types.includes('insights')}
                         onCheckedChange={(checked) => {
                           if (checked) {
                             setEditingPost({ ...editingPost, blog_types: [...editingPost.blog_types, 'insights'] });
                           } else {
                             setEditingPost({ ...editingPost, blog_types: editingPost.blog_types.filter(t => t !== 'insights') });
                           }
                         }}
                       />
                        <Label htmlFor="edit-blog-type-insights" className="font-normal cursor-pointer">
                          Partner Insights - Partner only
                        </Label>
                     </div>
                   </div>
                   <p className="text-xs text-muted-foreground">Select at least one blog type</p>
                 </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={updateBlogPost}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Picker Dialog */}
      <BlogImageUpload
        open={showImagePicker !== null}
        onOpenChange={(open) => !open && setShowImagePicker(null)}
        onImageInsert={(imageUrl) => {
          const url = imageUrl.replace(/!\[.*?\]\((.*?)\)/, '$1');
          if (showImagePicker === 'new' && newEditorRef.current) {
            newEditorRef.current.insertImage(url);
          } else if (showImagePicker === 'edit' && editEditorRef.current) {
            editEditorRef.current.insertImage(url);
          }
          setShowImagePicker(null);
        }}
      />
    </div>
    </>
  );
};

export default BlogManagement;